import { useTranslation } from 'react-i18next';
import type { Cohort } from '@/services/adminMetricsService';

interface CohortMatrixProps {
  cohorts: Cohort[];
}

/**
 * Retention matrix: rows = cohort (month + currency), cols = month_n.
 * Mobile: overflow-x-auto (sanctioned exception for dense data grids per §21.7).
 * The container must NEVER be hidden on any viewport.
 */
export function CohortMatrix({ cohorts }: CohortMatrixProps) {
  const { t } = useTranslation();

  if (cohorts.length === 0) {
    return <p className="text-sm text-muted-foreground">{t('admin.metrics.cohorts.empty')}</p>;
  }

  // Determine max month_n across all cohorts
  const maxMonthN = cohorts.reduce((m, c) => {
    const last = c.points.at(-1)?.month_n ?? 0;
    return Math.max(m, last);
  }, 0);

  const monthColumns = Array.from({ length: maxMonthN + 1 }, (_, i) => i);

  return (
    <div data-testid="cohort-matrix" className="overflow-x-auto">
      <table className="min-w-full text-xs border-collapse">
        <thead>
          <tr>
            <th className="py-1.5 pr-3 text-left font-medium text-muted-foreground whitespace-nowrap">
              {t('admin.metrics.cohorts.cohort_col')}
            </th>
            {monthColumns.map((n) => (
              <th
                key={n}
                className="py-1.5 px-2 text-right font-medium text-muted-foreground whitespace-nowrap"
              >
                M{n}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {cohorts.map((cohort) => {
            const pointByMonth = new Map(cohort.points.map((p) => [p.month_n, p]));
            const m0Count = cohort.points.find((p) => p.month_n === 0)?.active_count ?? 1;
            return (
              <tr
                key={`${cohort.signup_cohort}-${cohort.currency}`}
                className="border-t border-border"
              >
                <td className="py-1.5 pr-3 text-muted-foreground whitespace-nowrap">
                  {cohort.signup_cohort.slice(0, 7)} {cohort.currency}
                </td>
                {monthColumns.map((n) => {
                  const pt = pointByMonth.get(n);
                  if (!pt) {
                    return (
                      <td key={n} className="py-1.5 px-2 text-right text-muted-foreground/40">
                        —
                      </td>
                    );
                  }
                  const retPct = m0Count > 0 ? Math.round((pt.active_count / m0Count) * 100) : 0;
                  return (
                    <td key={n} className="py-1.5 px-2 text-right tabular-nums">
                      {pt.active_count}
                      <span className="ml-1 text-muted-foreground">({retPct}%)</span>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
